import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { colmenasAPI, eventosAPI, tareasAPI } from '../api/client';
import { useTranslation } from 'react-i18next';
import VoiceInput from '../components/VoiceInput';

const SEMAFORO = {
  verde:    { emoji: '🟢', color: '#4A7C59' },
  amarillo: { emoji: '🟡', color: '#C97820' },
  rojo:     { emoji: '🔴', color: '#C0392B' },
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function OptionGroup({ label, options, value, onChange }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={S.fieldLabel}>{label}</div>
      <div style={S.optionRow}>
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            className="campo-btn"
            onClick={() => onChange(value === opt.value ? null : opt.value)}
            style={{
              ...S.optionBtn,
              background: value === opt.value ? '#E8922A' : 'white',
              color: value === opt.value ? 'white' : '#2C1A0E',
              borderColor: value === opt.value ? '#E8922A' : '#FAE0B0',
              fontWeight: value === opt.value ? 600 : 400,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Campo() {
  const { t } = useTranslation();

  const ACCIONES_RAPIDAS = [
    { key: 'inspeccion',   icon: '🔍', label: t('lote.tipoInspeccion'),   color: '#4A7C59' },
    { key: 'alimentacion', icon: '🍯', label: t('lote.tipoAlimentacion'), color: '#E8922A' },
    { key: 'tratamiento',  icon: '💊', label: t('lote.tipoTratamiento'),  color: '#C0392B' },
    { key: 'cosecha',      icon: '🫙', label: t('lote.tipoCosecha'),      color: '#C97820' },
    { key: 'nota',         icon: '📝', label: t('lote.tipoNota'),         color: '#5C3D2E' },
  ];

  const [colmenas, setColmenas] = useState([]);
  const [seleccionada, setSeleccionada] = useState(null);
  const [accion, setAccion] = useState(null);
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [exito, setExito] = useState('');
  const [tareaExito, setTareaExito] = useState('');
  const [tareaError, setTareaError] = useState('');
  const [loading, setLoading] = useState(true);
  const [masDetalles, setMasDetalles] = useState(false);

  const [fecha, setFecha] = useState(todayISO());
  const [fuerza, setFuerza] = useState(null);
  const [cria, setCria] = useState(null);
  const [reservas, setReservas] = useState(null);
  const [reinaVista, setReinaVista] = useState(null);
  const [problema, setProblema] = useState('');
  const [accionTomada, setAccionTomada] = useState('');
  const [comportamiento, setComportamiento] = useState(null);
  const [alimentoCantidad, setAlimentoCantidad] = useState('');
  const [alimentoUnidad, setAlimentoUnidad] = useState('kg');

  useEffect(() => {
    colmenasAPI.list().then(r => {
      setColmenas(r.data.filter(c => c.activa));
    }).finally(() => setLoading(false));
  }, []);

  const resetForm = () => {
    setNota('');
    setFecha(todayISO());
    setFuerza(null);
    setCria(null);
    setReservas(null);
    setReinaVista(null);
    setProblema('');
    setAccionTomada('');
    setComportamiento(null);
    setAlimentoCantidad('');
    setAlimentoUnidad('kg');
    setMasDetalles(false);
  };

  const registrar = async () => {
    if (!seleccionada || !accion) return;
    setGuardando(true);
    try {
      const payload = {
        tipo: accion.key,
        titulo: accion.label,
        colmena_id: seleccionada.id,
        fecha: fecha ? new Date(fecha).toISOString() : undefined,
      };

      if (accion.key === 'inspeccion') {
        payload.fuerza_colonia = fuerza || null;
        payload.cria = cria || null;
        payload.reservas = reservas || null;
        payload.reina_vista = reinaVista || null;
        payload.descripcion = problema || null;
        payload.accion_tomada = accionTomada || null;
        if (comportamiento) payload.comportamiento = Number(comportamiento);
        if (alimentoCantidad) {
          payload.alimento_cantidad = Number(alimentoCantidad);
          payload.alimento_unidad = alimentoUnidad;
        }
      } else {
        payload.descripcion = nota || null;
      }

      await eventosAPI.create(payload);
      setExito(t('campo.eventCreated', { action: accion.label, name: seleccionada.nombre }));
      resetForm();
      setAccion(null);
      setTimeout(() => setExito(''), 3000);
    } finally { setGuardando(false); }
  };

  const marcarTarea = async (descripcion) => {
    if (!seleccionada) return;
    try {
      await tareasAPI.create({
        descripcion,
        prioridad: 'alta',
        colmena_id: seleccionada.id,
      });
      setTareaError('');
      setTareaExito(t('campo.taskCreated', { name: seleccionada.nombre }));
      setTimeout(() => setTareaExito(''), 3000);
    } catch {
      setTareaExito('');
      setTareaError(t('campo.taskError'));
      setTimeout(() => setTareaError(''), 3000);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
      <div style={{ fontSize: 40 }}>🍯</div>
    </div>
  );

  const fuerzaOpts = [
    { value: 'baja',  label: t('campo.fuerzaBaja') },
    { value: 'media', label: t('campo.fuerzaMedia') },
    { value: 'alta',  label: t('campo.fuerzaAlta') },
  ];
  const criaOpts = [
    { value: 'buena',   label: t('campo.criaBuena') },
    { value: 'regular', label: t('campo.criaRegular') },
    { value: 'sin',     label: t('campo.criaSin') },
  ];
  const reservasOpts = [
    { value: 'buenas',   label: t('campo.reservasBuenas') },
    { value: 'escasas',  label: t('campo.reservasEscasas') },
    { value: 'sin',      label: t('campo.reservasSin') },
  ];
  const reinaOpts = [
    { value: 'si',          label: t('campo.reinaSi') },
    { value: 'no',          label: t('campo.reinaNo') },
    { value: 'no_buscada',  label: t('campo.reinaNoBuscada') },
  ];

  return (
    <div style={S.container}>
      <div style={S.header}>
        <h1 style={S.headerTitle}>🐝 {t('campo.title')}</h1>
      </div>

      {exito && <div style={S.exitoMsg}>{exito}</div>}

      <div style={S.section}>
        <div style={S.sectionLabel}>{t('campo.whichHive')}</div>
        <div className="grid-campo">
          {colmenas.map(c => {
            const s = SEMAFORO[c.semaforo] || SEMAFORO.verde;
            const sel = seleccionada?.id === c.id;
            return (
              <button key={c.id} className="campo-btn" onClick={() => setSeleccionada(c)}
                style={{ ...S.colmenaBtn, ...(sel ? S.colmenaBtnSel : {}) }}>
                <div style={{ fontSize: 20 }}>{s.emoji}</div>
                <div style={{ fontSize: 13, fontWeight: sel ? 600 : 400, marginTop: 4 }}>
                  {c.nombre}
                </div>
                {c.numero && <div style={{ fontSize: 11, color: '#8B6355' }}>#{c.numero}</div>}
              </button>
            );
          })}
        </div>
        {/* Salida a la ficha sin estorbar el flujo rápido: sólo aparece con una
            colmena ya seleccionada y por debajo de la rejilla de selección. */}
        {seleccionada && (
          <Link to={`/colmenas/${seleccionada.id}`} className="campo-btn" style={S.fichaLink}>
            📄 {t('campo.verFicha', { name: seleccionada.nombre })}
          </Link>
        )}
      </div>

      {seleccionada && (
        <div style={S.section}>
          <div style={S.sectionLabel}>{t('campo.whatDid')}</div>
          <div className="grid-campo">
            {ACCIONES_RAPIDAS.map(a => (
              <button key={a.key} className="campo-btn" onClick={() => { setAccion(a); resetForm(); }}
                style={{
                  ...S.accionBtn,
                  background: accion?.key === a.key ? a.color : 'white',
                  color: accion?.key === a.key ? 'white' : '#2C1A0E',
                  borderColor: a.color,
                }}>
                <div style={{ fontSize: 28 }}>{a.icon}</div>
                <div style={{ fontSize: 13, marginTop: 6, fontWeight: 500 }}>{a.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {accion && seleccionada && accion.key === 'inspeccion' && (
        <div style={S.section}>
          <div style={{ marginBottom: 20 }}>
            <div style={S.fieldLabel}>{t('campo.fechaLabel')}</div>
            <input
              type="date"
              value={fecha}
              onChange={e => setFecha(e.target.value)}
              style={S.dateInput}
            />
          </div>

          <OptionGroup
            label={t('campo.fuerzaLabel')}
            options={fuerzaOpts}
            value={fuerza}
            onChange={setFuerza}
          />
          <OptionGroup
            label={t('campo.criaLabel')}
            options={criaOpts}
            value={cria}
            onChange={setCria}
          />
          <OptionGroup
            label={t('campo.reservasLabel')}
            options={reservasOpts}
            value={reservas}
            onChange={setReservas}
          />
          <OptionGroup
            label={t('campo.reinaLabel')}
            options={reinaOpts}
            value={reinaVista}
            onChange={setReinaVista}
          />

          <div style={{ marginBottom: 20 }}>
            <div style={S.fieldLabel}>{t('campo.problemaLabel')}</div>
            <input
              type="text"
              value={problema}
              onChange={e => setProblema(e.target.value)}
              placeholder={t('campo.problemaPlaceholder')}
              style={S.shortInput}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={S.fieldLabel}>{t('campo.accionTomadaLabel')}</div>
            <input
              type="text"
              value={accionTomada}
              onChange={e => setAccionTomada(e.target.value)}
              placeholder={t('campo.accionTomadaPlaceholder')}
              style={S.shortInput}
            />
            <VoiceInput onAppend={(text) => setAccionTomada(v => (v ? v + ' ' : '') + text)} />
          </div>

          <button
            type="button"
            className="campo-btn"
            onClick={() => setMasDetalles(v => !v)}
            style={S.toggleBtn}
          >
            {masDetalles ? t('campo.menosDetalles') : t('campo.masDetalles')}
          </button>

          {masDetalles && (
            <div style={S.detallesBox}>
              <div style={{ marginBottom: 16 }}>
                <div style={S.fieldLabel}>{t('campo.comportamientoLabel')}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[1, 2, 3, 4].map(n => (
                    <button
                      key={n}
                      type="button"
                      className="campo-btn"
                      onClick={() => setComportamiento(comportamiento === n ? null : n)}
                      style={{
                        ...S.numBtn,
                        background: comportamiento === n ? '#E8922A' : 'white',
                        color: comportamiento === n ? 'white' : '#2C1A0E',
                        borderColor: comportamiento === n ? '#E8922A' : '#FAE0B0',
                      }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={S.fieldLabel}>{t('campo.alimentoLabel')}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={alimentoCantidad}
                    onChange={e => setAlimentoCantidad(e.target.value)}
                    placeholder="0"
                    style={{ ...S.shortInput, flex: 1 }}
                  />
                  <select
                    value={alimentoUnidad}
                    onChange={e => setAlimentoUnidad(e.target.value)}
                    style={S.selectInput}
                  >
                    <option value="kg">kg</option>
                    <option value="L">L</option>
                  </select>
                </div>
              </div>

              <div>
                <div style={S.fieldLabel}>{t('campo.notasAdicionalesLabel')}</div>
                <textarea
                  value={nota}
                  onChange={e => setNota(e.target.value)}
                  placeholder={t('campo.notePlaceholder')}
                  style={S.notaInput}
                />
                <VoiceInput onAppend={(text) => setNota(v => (v ? v + ' ' : '') + text)} />
              </div>
            </div>
          )}

          <button className="campo-btn" onClick={registrar} disabled={guardando} style={{ ...S.registrarBtn, marginTop: 8 }}>
            {guardando ? '...' : t('campo.registerBtn', { action: accion.label })}
          </button>
        </div>
      )}

      {accion && seleccionada && accion.key !== 'inspeccion' && (
        <div style={S.section}>
          <div style={S.sectionLabel}>{t('campo.note')}</div>
          <textarea
            value={nota}
            onChange={e => setNota(e.target.value)}
            placeholder={t('campo.notePlaceholder')}
            style={S.notaInput}
          />
          <VoiceInput onAppend={(text) => setNota(v => (v ? v + ' ' : '') + text)} />
          <button className="campo-btn" onClick={registrar} disabled={guardando} style={S.registrarBtn}>
            {guardando ? '...' : t('campo.registerBtn', { action: accion.label })}
          </button>
        </div>
      )}

      {seleccionada && (
        <div style={S.section}>
          <div style={S.sectionLabel}>{t('campo.urgentTask')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { key: 'revisarReina',   label: t('campo.taskRevisarReina') },
              { key: 'alimentar',      label: t('campo.taskAlimentar') },
              { key: 'tratarVarroa',   label: t('campo.taskTratarVarroa') },
              { key: 'agregarAlza',    label: t('campo.taskAgregarAlza') },
              { key: 'reducirPiquera', label: t('campo.taskReducirPiquera') },
            ].map(tsk => (
              <button key={tsk.key} className="campo-btn" onClick={() => marcarTarea(tsk.label)} style={S.tareaRapidaBtn}>
                + {tsk.label}
              </button>
            ))}
          </div>
          {tareaExito && <div style={{ ...S.exitoMsg, marginTop: 10, marginBottom: 0 }}>{tareaExito}</div>}
          {tareaError && <div style={{ ...S.errorMsg, marginTop: 10, marginBottom: 0 }}>{tareaError}</div>}
        </div>
      )}
    </div>
  );
}

const S = {
  container: { maxWidth: 500, margin: '0 auto', padding: '0 16px 40px' },
  header: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: '2px solid #FAE0B0' },
  headerTitle: { fontSize: 26, fontFamily: "'DM Sans', sans-serif", color: '#2C1A0E', margin: 0 },
  exitoMsg: { background: '#E8F5EC', color: '#4A7C59', padding: '12px 16px', borderRadius: 12, marginBottom: 16, fontSize: 15, fontWeight: 500, textAlign: 'center' },
  errorMsg: { background: '#FDEDEC', color: '#C0392B', padding: '12px 16px', borderRadius: 12, marginBottom: 16, fontSize: 15, fontWeight: 500, textAlign: 'center' },
  section: { marginBottom: 24 },
  sectionLabel: { fontSize: 13, fontWeight: 600, color: '#8B6355', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 },
  fieldLabel: { fontSize: 13, fontWeight: 600, color: '#5C3D2E', marginBottom: 8 },
  colmenaBtn: { padding: '14px 8px', borderRadius: 14, border: '2px solid #FAE0B0', background: 'white', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' },
  colmenaBtnSel: { background: '#FDF0DC', borderColor: '#E8922A', transform: 'scale(1.04)' },
  accionBtn: { padding: '16px 8px', borderRadius: 14, border: '2px solid', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' },
  optionRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  optionBtn: { flex: '1 1 0', minWidth: 80, padding: '14px 8px', borderRadius: 12, border: '2px solid', cursor: 'pointer', fontSize: 15, textAlign: 'center', transition: 'all 0.15s' },
  numBtn: { width: 52, height: 52, borderRadius: 12, border: '2px solid', cursor: 'pointer', fontSize: 18, fontWeight: 600, textAlign: 'center', transition: 'all 0.15s' },
  dateInput: { width: '100%', padding: '13px 14px', borderRadius: 12, border: '1.5px solid #FAE0B0', fontSize: 16, fontFamily: 'inherit', boxSizing: 'border-box', background: 'white' },
  shortInput: { width: '100%', padding: '13px 14px', borderRadius: 12, border: '1.5px solid #FAE0B0', fontSize: 15, fontFamily: 'inherit', boxSizing: 'border-box', background: 'white' },
  selectInput: { padding: '13px 12px', borderRadius: 12, border: '1.5px solid #FAE0B0', fontSize: 15, fontFamily: 'inherit', background: 'white', minWidth: 64 },
  notaInput: { width: '100%', minHeight: 80, padding: '12px 14px', borderRadius: 12, border: '1.5px solid #FAE0B0', fontSize: 15, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: 12 },
  registrarBtn: { width: '100%', padding: '16px', background: '#E8922A', color: 'white', border: 'none', borderRadius: 14, fontSize: 17, fontWeight: 600, cursor: 'pointer' },
  tareaRapidaBtn: { padding: '12px 16px', background: 'white', border: '1.5px solid #FAE0B0', borderRadius: 12, fontSize: 14, cursor: 'pointer', textAlign: 'left', color: '#2C1A0E' },
  fichaLink: { display: 'block', marginTop: 12, padding: '12px 16px', background: 'white', border: '1.5px solid #FAE0B0', borderRadius: 12, fontSize: 14, textAlign: 'center', color: '#5C3D2E', textDecoration: 'none', fontWeight: 500 },
  toggleBtn: { width: '100%', padding: '13px', background: '#FDF0DC', color: '#5C3D2E', border: '1.5px dashed #E8922A', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 16 },
  detallesBox: { background: '#FAFAF8', border: '1.5px solid #FAE0B0', borderRadius: 14, padding: '16px', marginBottom: 16 },
};
